import { api, errMsg } from "@/lib/config";
import React, { useState, useEffect } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, UserPlus, Mail, Lock, KeyRound, CheckCircle2 } from "lucide-react";
import { useCustomer } from "@/lib/customerAuth";

export function CustomerLogin() {
  const { user, login, requestMagicLink } = useCustomer();
  const nav = useNavigate();
  const [mode, setMode] = useState("magic"); // "magic" | "password"
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  if (user) return <Navigate to="/area" replace />;

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(f.email, f.password); toast.success("Bentornato!"); nav("/area"); }
    catch (err) { toast.error(errMsg(err, "Credenziali non valide")); }
    finally { setBusy(false); }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    if (!f.email) return toast.error("Inserisci la tua email");
    setBusy(true);
    try {
      const r = await requestMagicLink(f.email);
      setMagicSent(true);
      toast.success(r?.message || "Se l'email è valida, riceverai un link di accesso.");
    } catch (err) {
      toast.error(errMsg(err, "Errore nell'invio del link"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:block md:w-1/2 relative overflow-hidden bg-[#0a0a0a] blueprint-grid">
        <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_30%_50%,rgba(255,75,0,0.12),transparent_60%)]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14">
          <div className="font-display text-2xl"><img src="/assets/logo/gefion.png" alt="Gefion Evolution" className="h-10 w-auto object-contain brightness-0 invert" /></div>
          <div>
            <div className="font-mono-tech text-white/40 mb-3">◐ Area riservata clienti</div>
            <div className="font-display text-white text-6xl leading-[0.9]">I tuoi<br/>impianti.</div>
            <div className="text-white/50 mt-6 max-w-sm">Ordini shop, ticket assistenza, storico interventi e stato dei tuoi impianti in un unico posto.</div>
          </div>
          <div className="font-mono-tech text-white/30 text-[10px]">Gefion Care · 24/7</div>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-sm">
          <div className="num-chip mb-6"><span className="dot" /> Accedi come cliente</div>
          <div className="font-display text-4xl mb-8">Entra.</div>

          {/* Mode toggle */}
          <div className="flex gap-2 mb-8 border-b border-[#1a1a1a]">
            <button
              type="button"
              onClick={() => { setMode("magic"); setMagicSent(false); }}
              data-testid="cust-mode-magic"
              className={`font-mono-tech text-[11px] px-4 py-3 border-b-2 -mb-px flex items-center gap-2 ${mode === "magic" ? "border-[#ff4b00] text-[#ff4b00]" : "border-transparent text-white/50 hover:text-white"}`}
            >
              <KeyRound size={12} /> Magic Link
            </button>
            <button
              type="button"
              onClick={() => { setMode("password"); setMagicSent(false); }}
              data-testid="cust-mode-password"
              className={`font-mono-tech text-[11px] px-4 py-3 border-b-2 -mb-px flex items-center gap-2 ${mode === "password" ? "border-[#ff4b00] text-[#ff4b00]" : "border-transparent text-white/50 hover:text-white"}`}
            >
              <Lock size={12} /> Password
            </button>
          </div>

          {mode === "magic" && !magicSent && (
            <form onSubmit={submitMagic}>
              <div className="text-white/60 text-sm mb-6">
                Inserisci la tua email — ti invieremo un <b className="text-white">link di accesso sicuro</b> valido per 20 minuti. Nessuna password da ricordare.
              </div>
              <div>
                <label className="field-label">Email</label>
                <input required type="email" data-testid="cust-magic-email" className="field mt-2" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="tu@azienda.it" />
              </div>
              <button disabled={busy} data-testid="cust-magic-submit" className="btn-primary w-full justify-center mt-8">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={14} /> Invia il link</>}
              </button>
              <div className="mt-6 pt-6 border-t border-[#1a1a1a]">
                <div className="font-mono-tech text-[10px] text-white/40 mb-2">◐ COME FUNZIONA</div>
                <ol className="space-y-1.5 text-white/50 text-[11px]">
                  <li><span className="text-[#ff4b00] font-mono-tech mr-2">01</span> Inserisci la tua email</li>
                  <li><span className="text-[#ff4b00] font-mono-tech mr-2">02</span> Controlla la casella di posta</li>
                  <li><span className="text-[#ff4b00] font-mono-tech mr-2">03</span> Clicca il link per accedere</li>
                </ol>
              </div>
            </form>
          )}

          {mode === "magic" && magicSent && (
            <div data-testid="cust-magic-sent" className="border border-[#22c55e]/30 bg-[#22c55e]/5 p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={24} className="text-[#22c55e] shrink-0" />
                <div>
                  <div className="font-display text-xl text-white mb-2">Controlla la tua email</div>
                  <div className="text-white/70 text-sm">Se <b className="text-white">{f.email}</b> è associata a un account o alle tue attività su Gefion, riceverai un link di accesso entro pochi minuti.</div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#1a1a1a]">
                <div className="font-mono-tech text-[10px] text-white/40 mb-3">◐ NON HAI RICEVUTO NULLA?</div>
                <ul className="space-y-2 text-white/60 text-[11px]">
                  <li>◐ Controlla la cartella <b>Spam</b> o <b>Promozioni</b></li>
                  <li>◐ Verifica di aver inserito l&apos;email corretta</li>
                  <li>◐ Attendi qualche minuto e riprova</li>
                </ul>
                <button
                  onClick={() => { setMagicSent(false); setF({ ...f, email: "" }); }}
                  data-testid="cust-magic-again"
                  className="btn-ghost w-full justify-center mt-5"
                >
                  Riprova con un&apos;altra email
                </button>
              </div>
            </div>
          )}

          {mode === "password" && (
            <form onSubmit={submitPassword}>
              <div className="space-y-6">
                <div><label className="field-label">Email</label><input required type="email" data-testid="cust-login-email" className="field mt-2" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div><label className="field-label">Password</label><input required type="password" data-testid="cust-login-password" className="field mt-2" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
              </div>
              <button disabled={busy} data-testid="cust-login-submit" className="btn-primary w-full justify-center mt-10">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <><LogIn size={14} /> Accedi</>}
              </button>
              <div className="text-center mt-6 text-sm text-white/60">
                Non hai un account? <Link to="/area/registrati" className="text-[#ff4b00] hover:underline">Registrati gratis</Link>
              </div>
            </form>
          )}

          <div className="text-center mt-8">
            <Link to="/" className="font-mono-tech text-[10px] text-white/40 hover:text-white">← Torna al sito</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustomerRegister() {
  const { user, register } = useCustomer();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", password: "", password2: "" });
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/area" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) return toast.error("Accetta la privacy policy per procedere");
    if (f.password.length < 6) return toast.error("Password minimo 6 caratteri");
    if (f.password !== f.password2) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await register({ name: f.name, email: f.email, phone: f.phone, company: f.company, password: f.password });
      try { await api.post("/privacy/consent-log", { kind: "form", form: "customer_register", email: f.email }); } catch { /* consent-log is best-effort */ }
      toast.success("Account creato!"); nav("/area");
    } catch (err) { toast.error(errMsg(err, "Errore")); }
    finally { setBusy(false); }
  };
  return (
    <div className="pt-24 pb-24">
      <div className="max-w-md mx-auto px-6 pt-8">
        <div className="num-chip mb-6"><span className="dot" /> Nuovo cliente</div>
        <div className="font-display text-4xl md:text-5xl mb-8 leading-[0.95]">Registrati.</div>
        <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 mb-8">
          <div className="flex items-start gap-3">
            <KeyRound size={16} className="text-[#ff4b00] mt-0.5 shrink-0" />
            <div className="text-white/60 text-sm">
              <b className="text-white">Preferisci senza password?</b> Puoi accedere direttamente via <Link to="/area/login" className="text-[#ff4b00] hover:underline">Magic Link</Link> — nessuna registrazione necessaria.
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div><label className="field-label">Nome e cognome *</label><input required data-testid="cust-reg-name" className="field mt-2" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="field-label">Email *</label><input required type="email" data-testid="cust-reg-email" className="field mt-2" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><label className="field-label">Telefono</label><input data-testid="cust-reg-phone" className="field mt-2" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><label className="field-label">Azienda (opz.)</label><input data-testid="cust-reg-company" className="field mt-2" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></div>
          <div><label className="field-label">Password *</label><input required type="password" data-testid="cust-reg-password" className="field mt-2" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
          <div><label className="field-label">Conferma password *</label><input required type="password" data-testid="cust-reg-password2" className="field mt-2" value={f.password2} onChange={(e) => setF({ ...f, password2: e.target.value })} /></div>
          <label className="flex items-start gap-3 text-white/70 text-sm cursor-pointer">
            <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="cust-reg-privacy" className="mt-1 accent-[#ff4b00]" />
            <span>Ho letto e accetto la <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link>. Autorizzo il trattamento dei miei dati per la creazione dell&apos;account. *</span>
          </label>
          <button disabled={busy} data-testid="cust-reg-submit" className="btn-primary w-full justify-center mt-6">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><UserPlus size={14} /> Crea account</>}
          </button>
          <div className="text-center text-sm text-white/60">
            Hai già un account? <Link to="/area/login" className="text-[#ff4b00] hover:underline">Accedi</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Magic link verification page: /area/verifica?token=XXX ─────
export function CustomerMagicVerify() {
  const { verifyMagicLink, user } = useCustomer();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("verifying");
  const [errorMsg, setErrorMsg] = useState("");
  const doneRef = React.useRef(false);

  useEffect(() => {
    // Guard: React.StrictMode fires effects twice in dev; magic-link is single-use
    if (doneRef.current) return;
    doneRef.current = true;

    const token = params.get("token");
    if (!token || token.length < 20) {
      setState("error");
      setErrorMsg("Link non valido: il codice di accesso è mancante o incompleto.");
      return;
    }
    if (user) { nav("/area", { replace: true }); return; }

    verifyMagicLink(token)
      .then(() => {
        setState("success");
        setTimeout(() => nav("/area", { replace: true }), 1200);
      })
      .catch((err) => {
        setState("error");
        // Friendly error messages
        const raw = errMsg(err, "");
        const friendly = raw && (
          raw.includes("scaduto") ||
          raw.includes("già utilizzato") ||
          raw.includes("non valido")
        ) ? raw : "Link non valido o scaduto. Richiedi un nuovo link di accesso.";
        setErrorMsg(friendly);
      });
  }, [params, user, verifyMagicLink, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="num-chip mb-6"><span className="dot" /> Verifica in corso</div>
        {state === "verifying" && (
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-8 text-center" data-testid="magic-verifying">
            <Loader2 size={32} className="animate-spin text-[#ff4b00] mx-auto mb-6" />
            <div className="font-display text-2xl text-white mb-2">Sto verificando il link...</div>
            <div className="font-mono-tech text-white/40 text-[11px]">Attendere qualche secondo.</div>
          </div>
        )}
        {state === "success" && (
          <div className="border border-[#22c55e]/30 bg-[#22c55e]/5 p-8 text-center" data-testid="magic-success">
            <CheckCircle2 size={32} className="text-[#22c55e] mx-auto mb-6" />
            <div className="font-display text-2xl text-white mb-2">Accesso effettuato!</div>
            <div className="text-white/60 text-sm">Ti stiamo portando all&apos;area riservata...</div>
          </div>
        )}
        {state === "error" && (
          <div className="border border-red-500/30 bg-red-500/5 p-8" data-testid="magic-error">
            <div className="font-display text-2xl text-white mb-3">Link non utilizzabile</div>
            <div className="text-white/70 text-sm mb-6">{errorMsg}</div>
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">◐ COSA FARE ORA</div>
            <ul className="space-y-2 text-white/60 text-[11px] mb-6">
              <li>◐ Il link potrebbe essere scaduto (validità 20 min)</li>
              <li>◐ Oppure è già stato usato una volta</li>
              <li>◐ Richiedi un nuovo link dalla pagina di accesso</li>
            </ul>
            <Link to="/area/login" data-testid="magic-error-back" className="btn-primary w-full justify-center">
              → Richiedi nuovo link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
